// ============================================================================
//  MileMarker.jsx — the app shell.
//
//  Owns the header, the tab bar, route-driven view switching, the one
//  loadState call and the mutation callbacks that write back into `st`.
//  Every screen and modal lives in src/screens or src/components.
// ============================================================================

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { LogOut, UserPlus } from "lucide-react";
import { useAuth, signOut } from "./auth/AuthContext.jsx";
import {
  loadState, updateProfile, setProfileSuspended, acceptVisibilityNotice,
} from "./lib/storage.js";
import { isDemo } from "./demo/demoMode.js";
import { C, fonts, display, wordmark, page, smallBtn, smallGhost } from "./theme/tokens.js";
import { LOGO_EQUILIBRIUM, LOGO_MEN_OF_IRON } from "./theme/logos.js";
import { Mark } from "./theme/primitives.jsx";
import { rank } from "./lib/roles.js";
import Dashboard from "./screens/Dashboard.jsx";
import TeamDashboard from "./screens/TeamDashboard.jsx";
import ManageConsole from "./screens/ManageConsole.jsx";
import BulkInvite from "./screens/BulkInvite.jsx";
import Help from "./screens/Help.jsx";
import ProfileModal from "./components/ProfileModal.jsx";
import InviteModal from "./components/InviteModal.jsx";
import VisibilityNotice from "./components/VisibilityNotice.jsx";

/** Shown if a man reaches /manage directly without the rank for it. */
function NoAccess({ onBack }) {
  return (
    <div style={{ maxWidth: 520, margin: "60px auto", padding: "0 24px", textAlign: "center" }}>
      <div style={{ ...display, fontSize: 22, color: C.text, marginBottom: 8 }}>
        That page is for team admins
      </div>
      <p style={{ fontSize: 13.5, color: C.sub, lineHeight: 1.6 }}>
        If you think you should have access, ask your admin.
      </p>
      <button onClick={onBack} style={{ ...smallBtn, marginTop: 16 }}>
        Back to my dashboard
      </button>
    </div>
  );
}








// [key, label, path, minimum rank]. Manage is hidden below Team Admin — the
// server refuses those actions anyway, so offering the tab would only bait a
// man into an error.
const TABS = [
  ["mentee", "My Dashboard", "/", 0],
  ["team",   "Team",         "/team", 0],
  ["manage", "Manage",       "/manage", 2],
  ["help",   "Help",         "/help", 0],
];

export default function App({ initialView }) {
  const { session, profile } = useAuth();
  const nav = useNavigate();
  const { personId: routePersonId } = useParams();

  const SELF = session?.user?.id;
  const role = profile?.role ?? "mentee";
  const demo = isDemo();
  const base = demo ? "/demo" : "";
  const myRank = rank(role);

  const [st, setStRaw] = useState(null);
  const [loadErr, setLoadErr] = useState("");
  const [view, setView] = useState(initialView ?? "mentee");
  const [target, setTarget] = useState(routePersonId ?? null);
  const [editingProfile, setEditingProfile] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);

  // One-time team-visibility notice. Driven off the profile column rather than
  // localStorage so it survives a new phone and leaves a record he saw it.
  // `noticeAck` lets us close it immediately without waiting for a profile refetch.
  const [noticeAck, setNoticeAck] = useState(false);
  const showNotice =
    !!profile && !profile.accepted_visibility_notice_at && !noticeAck;

  const acceptNotice = useCallback(async () => {
    await acceptVisibilityNotice(SELF);
    setNoticeAck(true);
  }, [SELF]);

  useEffect(() => { setTarget(routePersonId ?? null); }, [routePersonId]);
  // Always mirror the route, including back to the default. React Router keeps
  // this same component instance alive across /, /team and /help, so a
  // truthiness guard here would strand the previous view on screen after
  // navigating home.
  useEffect(() => { setView(initialView ?? "mentee"); }, [initialView]);

  // Refetch the world. The management console calls this after every RPC:
  // one change can ripple across profiles, group_leaders and groups at once,
  // so optimistic patching across four interlocking tables is not worth it.
  const reload = useCallback(async () => {
    if (!SELF) return;
    try {
      setStRaw(await loadState(SELF));
    } catch (e) {
      setLoadErr(e.message || "Failed to load.");
    }
  }, [SELF]);

  useEffect(() => {
    if (!SELF) return;
    let cancelled = false;
    loadState(SELF)
      .then((s) => { if (!cancelled) setStRaw(s); })
      .catch((e) => { if (!cancelled) setLoadErr(e.message || "Failed to load."); });
    return () => { cancelled = true; };
  }, [SELF]);

  const setSt = useCallback((u) => {
    setStRaw((prev) => (typeof u === "function" ? u(prev) : u));
  }, []);

  // The viewer as the roles helpers expect him: his own person row carries
  // group_id and is_suspended, which the auth profile alone does not.
  const me = st?.people?.[SELF]
    ? { ...st.people[SELF], role }
    : { id: SELF, role, group_id: profile?.group_id ?? null, is_suspended: false };

  const saveProfile = useCallback(async (data) => {
    await updateProfile(SELF, data);
    setSt((prev) => {
      const n = structuredClone(prev);
      n.people[SELF] = { ...n.people[SELF], ...data };
      return n;
    });
    setEditingProfile(false);
  }, [setSt, SELF]);

  if (loadErr) {
    return (
      <div style={{ ...page, display: "grid", placeItems: "center", minHeight: "100vh" }}>
        <style>{fonts}</style>
        <div style={{ textAlign: "center", color: C.redDk, maxWidth: 460, padding: 20 }}>
          <div style={{ ...display, fontSize: 18, marginBottom: 8 }}>Couldn't load your data</div>
          <div style={{ fontSize: 13, color: C.sub }}>{loadErr}</div>
        </div>
      </div>
    );
  }

  if (!st || !SELF) {
    return (
      <div style={{ ...page, display: "grid", placeItems: "center", minHeight: "100vh" }}>
        <style>{fonts}</style>
        <div style={{ textAlign: "center", color: C.sub }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 14,
            animation: "mm-pulse 1.4s ease-in-out infinite" }}><Mark size={36} /></div>
          <div style={{ ...display, fontSize: 16 }}>Loading your game plan…</div>
        </div>
      </div>
    );
  }

  return (
    <div style={page}>
      <style>{fonts}</style>
      <div style={{ borderBottom: `1px solid ${C.line}`, background: "rgba(255,255,255,0.92)",
        backdropFilter: "blur(10px)", position: "sticky", top: 0, zIndex: 50 }}>
        <div className="mm-header" style={{ maxWidth: 1080, margin: "0 auto", padding: "14px 24px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <img src={LOGO_EQUILIBRIUM} alt="Equilibrium"
              style={{ height: 34, width: "auto", display: "block" }} />
            <div style={{ borderLeft: `1px solid ${C.line}`, paddingLeft: 14 }}>
              <div style={{ ...wordmark, fontSize: 14, color: C.ink, lineHeight: 1 }}>
                MILE MARKER
              </div>
              <div style={{ fontSize: 9, letterSpacing: ".18em", color: C.faint, marginTop: 4 }}>
                5F GAME PLAN
              </div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ display: "flex", gap: 4, background: C.shell, padding: 4, borderRadius: 10,
              border: `1px solid ${C.line}` }}>
              {TABS.filter(([, , , minRank]) => myRank >= (minRank ?? 0))
                .map(([k, lab, path]) => (
                <button key={k} className="mm-tab"
                  onClick={() => nav(path === "/" ? (base || "/") : base + path)} style={{
                  padding: "7px 16px", borderRadius: 7, cursor: "pointer", fontSize: 13, fontWeight: 700,
                  border: "none", background: view === k ? C.red : "transparent",
                  color: view === k ? "#fff" : C.sub, letterSpacing: ".02em",
                  whiteSpace: "nowrap" }}>{lab}</button>
              ))}
            </div>
            {(role === "admin" || role === "mentor") && (
              <button onClick={() => setInviteOpen(true)} style={smallBtn} title="Invite a new man">
                <UserPlus size={14} /> Invite
              </button>
            )}
            {!demo && (
              <button onClick={signOut} style={smallGhost} title="Sign out">
                <LogOut size={14} />
              </button>
            )}
          </div>
        </div>
      </div>

      <div style={{ paddingTop: 4 }}>
        {view === "mentee" && (
          <Dashboard st={st} setSt={setSt} personId={SELF} readOnly={false}
            onEditProfile={() => setEditingProfile(true)} />
        )}
        {view === "team" && !target && (
          <TeamDashboard st={st} me={me} selfId={SELF}
            onOpen={(id) => nav(`${base}/team/${id}`)}
            onToggleSuspend={async (id, isSuspended) => {
              await setProfileSuspended(id, isSuspended);
              setSt((prev) => {
                const n = structuredClone(prev);
                if (n.people[id]) n.people[id].is_suspended = isSuspended;
                return n;
              });
            }} />
        )}
        {view === "team" && target && (
          <Dashboard st={st} setSt={setSt} personId={target}
            readOnly={target !== SELF}
            onEditProfile={() => setEditingProfile(true)}
            onBack={() => nav(`${base}/team`)} />
        )}
        {view === "manage" && (
          myRank >= 2
            ? <ManageConsole st={st} me={me} selfId={SELF} onChanged={reload}
                onInvite={() => setInviteOpen(true)}
                onBulkInvite={() => nav(`${base}/manage/invite`)} />
            : <NoAccess onBack={() => nav(base || "/")} />
        )}
        {view === "bulk" && (
          myRank >= 2
            ? <BulkInvite st={st} me={me} onChanged={reload}
                onBack={() => nav(`${base}/manage`)} />
            : <NoAccess onBack={() => nav(base || "/")} />
        )}
        {view === "help" && <Help role={role} />}
      </div>

      {editingProfile && (
        <ProfileModal person={st.people[SELF]} onSave={saveProfile}
          onClose={() => setEditingProfile(false)} />
      )}

      {inviteOpen && (
        <InviteModal callerGroupId={profile?.group_id}
          onClose={() => setInviteOpen(false)} />
      )}

      {showNotice && <VisibilityNotice onAcknowledge={acceptNotice} />}

      <div style={{ textAlign: "center", padding: "40px 0 50px", display: "flex",
        flexDirection: "column", alignItems: "center", gap: 16 }}>
        <div style={{ fontSize: 11, color: C.faint, letterSpacing: ".03em" }}>
          Iron sharpens iron · Faith · Family · Friends · Fitness · Finances
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
          paddingTop: 18, borderTop: `1px solid ${C.line}`, width: "100%", maxWidth: 240 }}>
          <span style={{ fontSize: 9.5, letterSpacing: ".18em", color: C.faint, textTransform: "uppercase" }}>
            A ministry of
          </span>
          <img src={LOGO_MEN_OF_IRON} alt="Men of Iron"
            style={{ height: 52, width: "auto", display: "block", opacity: 0.9 }} />
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  STYLES                                                             */
/* ------------------------------------------------------------------ */




